import { useEffect, useState } from 'react';
import { cn } from '@hero/web-lib';

export interface AppLogoProps {
  name: string;
  /** Public logo path served by the dashboard, e.g. `/images/apps/snb-logo.svg`. */
  logo?: string;
  className?: string;
  /** Inline icon — no tile background or inner padding (e.g. order action rows). */
  compact?: boolean;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();
}

/**
 * Renders an app's brand logo from a local public path
 * (`/images/apps/...`), falling back to a tinted initials tile if the
 * image is missing or fails to load.
 */
export function AppLogo({ name, logo, className = '', compact = false }: AppLogoProps) {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [logo]);

  if (!logo || failed) {
    if (compact) {
      return (
        <div
          className={cn(
            'flex items-center justify-center rounded bg-primary-50 text-[8px] font-bold text-primary',
            className,
          )}>
          {initials(name)}
        </div>
      );
    }

    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-xl bg-primary-50 text-sm font-bold text-primary',
          className,
        )}>
        {initials(name)}
      </div>
    );
  }

  if (compact) {
    return (
      <img src={logo} alt={name} className={cn('object-contain', className)} onError={() => setFailed(true)} />
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center overflow-hidden rounded-xl bg-white',
        className,
      )}>
      <img
        src={logo}
        alt={name}
        className="h-full w-full object-contain p-1"
        onError={() => setFailed(true)}
      />
    </div>
  );
}
